import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import reactionService from 'services/reactionService';
import { EmojiReaction } from 'v2/components/viewer/EmojiMenu/EmojiMenu';

export const reactToDoc = createAsyncThunk(
  'scribe/reactToDoc',
  async ({ docId, identifier }: { docId: string; identifier: EmojiReaction }) => {
    const res = await reactionService.create(identifier, docId);

    return res;
  }
);

export const getReactionsForDoc = createAsyncThunk(
  'scribe/getReactionsForDoc',
  async ({ docId, superOrganizationId }: { docId: string; superOrganizationId?: string }) => {
    return reactionService.getReactionsForDoc(docId, superOrganizationId);
  }
);

export const getUserReactionForDoc = createAsyncThunk(
  'scribe/getUserReactionForDoc',
  async ({ docId, authenticated }: { docId: string; authenticated: boolean }) => {
    const userReactionForDoc = await reactionService.getUserReactionForDoc(docId, authenticated);

    return userReactionForDoc;
  }
);

export const updateUserReactionForDoc = createAsyncThunk(
  'scribe/updateUserReactionForDoc',
  async ({ reactionId, docId, identifier }: { reactionId: string; docId: string; identifier: string }) => {
    const userReactionForDoc = await reactionService.update(reactionId, docId, identifier);

    return userReactionForDoc;
  }
);

export const deleteReaction = createAsyncThunk(
  'scribe/deleteReaction',
  async ({ reactionId, docId }: { reactionId: string; docId: string }) => {
    await reactionService.delete(reactionId, docId);

    return { reactionId };
  }
);

const INITIAL_STATE = {
  reactions: [],
  isLoading: false,
  loadingError: null,
  userReactionForDoc: null
};

const reactionsSlice = createSlice({
  name: 'reactions',
  initialState: { ...INITIAL_STATE },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(reactToDoc.fulfilled, (state, action) => {
        state.reactions = [...state.reactions, action.payload];
        state.userReactionForDoc = action.payload;
      })
      .addCase(getUserReactionForDoc.fulfilled, (state, action) => {
        const userReaction = Object.keys(action.payload).length !== 0 ? action.payload : null;

        state.userReactionForDoc = userReaction;
      })
      .addCase(updateUserReactionForDoc.fulfilled, (state, action) => {
        state.userReactionForDoc = action.payload;
        state.reactions = state.reactions.map((r) => {
          if (r.id === action.payload.id) {
            return action.payload;
          }
          return r;
        });
      })
      .addCase(getReactionsForDoc.fulfilled, (state, action) => {
        if (Array.isArray(action.payload)) {
          state.reactions = action.payload;
        } else {
          console.error('Reactions payload is not an array');
        }
        state.isLoading = false;
        state.loadingError = null;
      })
      .addCase(getReactionsForDoc.pending, (state) => {
        state.isLoading = true;
        state.loadingError = null;
      })
      .addCase(getReactionsForDoc.rejected, (state, action) => {
        state.isLoading = false;
        state.loadingError = action.payload;
      })
      .addCase(deleteReaction.fulfilled, (state, action) => {
        state.reactions = state.reactions.filter((r) => r.id !== action.payload.reactionId);
        state.userReactionForDoc = null;
      });
  }
});

export const selectReactions = createSelector(
  [
    (state) => state.reactions.reactions,
    (state) => state.reactions.isLoading,
    (state) => state.reactions.loadingError,
    (state) => state.reactions.userReactionForDoc
  ],
  (reactions, isLoading, loadingError, userReactionForDoc) => ({ reactions, isLoading, loadingError, userReactionForDoc })
);

export default reactionsSlice.reducer;
